<template>
  <div class="is-flex is-flex-direction-column mb-5">
    <div class="field has-addons">
      <div class="control has-icons-left is-rounded is-flex-grow-1">
        <span class="icon is-left">
          <i class="fas fa-search"></i>
        </span>
        <input
          ref="textInput"
          class="input is-rounded"
          type="search"
          placeholder="Search by ingredient name..."
          :value="ingredientMetaStore.search.q"
          style="z-index: 3"
          @input="ingredientMetaStore.setQueryFilter($event.target.value)"
        />
      </div>

      <div class="control">
        <button
          title="Advanced Filtering"
          class="button rounded-right"
          :class="{ 'is-active': showAdvanced, 'is-info': showAdvanced }"
          @click="showAdvanced = !showAdvanced"
        >
          <span class="icon is-left">
            <i class="fa-light fa-sliders-h"></i>
          </span>
          <span>Filters</span>
        </button>
      </div>
    </div>

    <div v-show="showAdvanced" class="box">
      <div class="is-flex is-align-items-center gap-3">
        <TagInput
          :model-value="ingredientMetaStore.search.tags"
          :all-tags="ingredientTagStore.allTags || []"
          :allow-new="false"
          placeholder="Filter by tag..."
          @update:model-value="ingredientMetaStore.setTagFilter"
          @advanced="(tag) => tagEditController.show(tag)"
        />

        <TagManager v-if="props.showTagManager" :tag-store="ingredientTagStore" />
      </div>

      <TagEditDialog
        v-if="editingTag"
        :id="editingTag.id"
        :name="editingTag.name"
        :color="editingTag.color"
        :count="editingTag.count"
        @save="(tag) => tagEditController.save(tag)"
        @cancel="(tag) => tagEditController.cancel(tag)"
        @destroy="(tag) => tagEditController.destroy(tag)"
      />

      <div
        v-if="props.userHasArchivedRecipes || hasAnyFilters"
        class="is-flex is-align-items-center mt-4"
      >
        <div v-if="props.userHasArchivedRecipes" class="field mb-0">
          <div class="control">
            <label class="checkbox" for="ingredient_meta_archived_filter">
              <input
                id="ingredient_meta_archived_filter"
                class="mr-2"
                type="checkbox"
                :checked="ingredientMetaStore.search.archived"
                @change="ingredientMetaStore.setSearchFilter('archived', $event.target.checked)"
              />
              Include archived recipes
            </label>
          </div>
        </div>

        <a
          v-if="hasAnyFilters"
          id="reset-filters"
          class="ml-auto"
          @click.prevent="ingredientMetaStore.resetFilters()"
        >
          <span class="icon-text">
            <span class="icon has-text-danger">
              <i class="fa fa-times"></i>
            </span>
            <span class="is-bold">Reset Filters</span>
          </span>
        </a>
      </div>
    </div>
  </div>

  <IngredientMetaListEdit
    :user-id="props.userId"
    @tag-click="ingredientMetaStore.addTagFilter"
    @sort-click="ingredientMetaStore.toggleSort"
  />

  <div
    v-if="ingredientMetaStore.ingredientMetas.length == 0"
    class="has-text-centered is-italic is-size-5"
  >
    No ingredients matched your search criteria
  </div>

  <PagerWidget
    v-if="ingredientMetaStore.loaded"
    :model-value="ingredientMetaStore.search.currentPage"
    :total-entries="ingredientMetaStore.search.totalEntries"
    :per-page="ingredientMetaStore.search.perPage"
    @update:model-value="ingredientMetaStore.loadIngredientMetaPage"
  />
</template>

<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import { onBeforeMount, watch } from "vue";
  import { useEventListener } from "@vueuse/core";

  import IngredientMetaListEdit from "./ingredient-meta-list-edit.vue";
  import TagInput from "./tag-input.vue";
  import TagEditDialog from "./tag-edit-dialog.vue";
  import TagManager from "./tag-manager.vue";
  import PagerWidget from "./pager-widget.vue";

  import { useIngredientMetaStore } from "./stores/ingredient-meta-store.js";
  import { useIngredientTagStore } from "./stores/ingredient-tag-store";
  import { useTagEditController } from "./lib/tag-edit-controller.js";
  import { tagsWithUpdatedTag } from "./stores/tag-store.js";

  const props = defineProps({
    userId: { type: String, required: true },
    userHasArchivedRecipes: { type: Boolean },
    showTagManager: { type: Boolean, default: true },

    preload: { type: Object, required: false, default: undefined },
  });

  const editingTag = _ref(null);

  const ingredientMetaStore = useIngredientMetaStore();
  const ingredientTagStore = useIngredientTagStore();

  const tagEditController = useTagEditController({
    store: ingredientTagStore,
    editingTag: (editingTag),
    tags: null,
  });

  /* Update tags in search filters when name/color changed */
  useEventListener(document, "ingredient-tag:update", (ev) => {
    const { oldTag, newTag } = ev.detail;
    ingredientMetaStore.search.tags = tagsWithUpdatedTag(
      ingredientMetaStore.search.tags,
      oldTag,
      newTag
    );
  });

  let showAdvanced = _ref(ingredientMetaStore.hasAdvancedFilters);
  watch(
    () => ingredientMetaStore.hasAdvancedFilters,
    (hasAdvancedFilters) => {
      if (hasAdvancedFilters) {
        showAdvanced.value = true;
      }
    }
  );

  const hasAnyFilters = _computed(() => {
    return ingredientMetaStore.hasQuery || ingredientMetaStore.hasAdvancedFilters;
  });

  onBeforeMount(() => {
    ingredientMetaStore.assignInitial({ userId: props.userId }, { patch: true });
    if (props.preload) {
      ingredientMetaStore.$patch({ loaded: true, ...props.preload });
    }

    ingredientTagStore.setUserId(props.userId);
    ingredientTagStore.loadAllTags();
  });
</script>
